import React, {useEffect} from 'react';
import {View, Text} from 'react-native';
import {SkillzButton} from '@skillz-rn/atoms';
import {DialogV3} from 'DialogV3';
import I18n, {_m} from 'INTL';
import SkillzImage from 'SkillzImage';
import {updateUserProperties} from 'UserPropertiesController';
import {stylesFn} from './BankWithdrawalPopup.styles';
import {Badge} from 'Badge';
import {ServerPaymentsResponse} from 'StoreInfo';

type BankWithdrawalPopupProps = {
  title: string;
  description: string;
  ctaText?: string;
  onPress?: () => void;
  onClose: () => void;
  isRTPEnabled: boolean;
  userPaymentData: ServerPaymentsResponse;
};

export function BankWithdrawalPopup({
  title,
  description,
  ctaText,
  onPress,
  onClose,
  isRTPEnabled,
  userPaymentData,
}: BankWithdrawalPopupProps) {
  const styles = stylesFn();

  useEffect(() => {
    const currentDate = new Date();
    updateUserProperties('withdrawal.lastTimeSeenBankPopup', currentDate.toISOString());
  }, []);

  const InstantBankingPopup = () => {
    const rtpData = userPaymentData?.aeropay?.rtp;
    const hasLinkedBanks = userPaymentData?.bank_accounts?.length > 0;
    if (!rtpData) {
      return null;
    }
    const title = hasLinkedBanks
      ? _m('Instant Bank\nWithdrawals Unlocked!')
      : _m('Instant Bank Withdrawals\n have arrived!');
    const description = hasLinkedBanks
      ? _m('Get your winnings in minutes, 24/7.\nNo waiting, no hassle.')
      : undefined;
    const subDescriptionTitle = hasLinkedBanks
      ? _m('EARLY ACCESS LIMITS:')
      : _m('LINK YOUR BANK TO UNLOCK:');
    const subDescription = hasLinkedBanks
      ? _m(
          '{0} instant withdrawals per day\nUp to {1} each',
          rtpData?.daily_frequency_count_limit,
          I18n.getCurrencyString(rtpData?.withdrawal_amount_limit, '$', false, false)
        )
      : _m('\u2022Payment in minutes\n\u2022Exclusive banking rewards\n\u2022Secure, hassle-free payouts');
    const ctaText = hasLinkedBanks ? _m('TRY IT NOW') : _m('WITHDRAW NOW!');
    return (
      <DialogV3
        onClose={onClose}
        headerContainerStyles={styles.headerContainer}
        headerImageComponent={
          <SkillzImage
            source={'icon_withdrawal_option_instant_banking.png'}
            style={{width: 104, height: 125}}
          />
        }
      >
        <Badge text={_m('NEW!')} style={{position: 'absolute', top: -200, left: 0, zIndex: 100}} />
        <Text style={styles.title} allowFontScaling={false}>
          {title}
        </Text>
        {description && (
          <Text style={styles.descriptionText} allowFontScaling={false}>
            {description}
          </Text>
        )}
        <Text style={styles.subDescriptionTextTitle} allowFontScaling={false}>
          {subDescriptionTitle}
        </Text>
        <Text style={styles.subDescriptionText} allowFontScaling={false}>
          {subDescription}
        </Text>
        {!hasLinkedBanks && (
          <SkillzImage
            source={'icon_bank_logos.svg'}
            style={styles.bankLogos}
          />
        )}

        {ctaText && onPress && (
          <SkillzButton
            text={ctaText}
            size={'L'}
            branding='Game'
            type='Tertiary'
            containerStyle={styles.ctaButtonContainer}
            onPress={onPress}
          />
        )}
      </DialogV3>
    );
  };

  if (isRTPEnabled) {
    return <InstantBankingPopup />;
  }

  return (
    <DialogV3
      onClose={onClose}
      headerContainerStyles={styles.headerContainer}
      headerImageComponent={
        <SkillzImage
          source={'image_bank_withdrawal_popup.png'}
          style={{width: 159.33, height: 135}}
        />
      }
    >
      <Text style={styles.title}>{title}</Text>

      <View style={styles.accentTextContainer}>
        <Badge text={_m('NEW!')} />
        <Text style={styles.accentText}>{_m('Fast Bank Withdrawals')}</Text>
      </View>

      <Text style={styles.descriptionText}>{description}</Text>

      {ctaText && onPress && (
        <SkillzButton
          text={ctaText}
          size={'L'}
          branding='Game'
          type='Tertiary'
          containerStyle={styles.ctaButtonContainer}
          onPress={onPress}
        />
      )}
    </DialogV3>
  );
}
